<i18n>
{
  "en": {
    "mostSoldItems": "Most sold products",
    "soldUnits": "Sold units",
    "margin": "Unit Margin",
    "KPILoadingError": "Error loading one or more KPIs"
  },
  "es": {
    "mostSoldItems": "Platos más vendidos",
    "soldUnits": "Unidades",
    "margin": "Margen Unitario",
    "KPILoadingError": "Error cargando uno o más indicadores"
  }
}
</i18n>

<template>
  <v-data-table
    :headers="headers"
    :items="items"
    :itemsPerPage="10"
    class="elevation-1"
    id="sales-table"
    mobile-breakpoint="0"
    @pagination="onPage"
  />
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import { Prop, Watch } from 'vue-property-decorator';
import { MapActions, MapGetters } from '@core/helpers/decorators';
import { sentryLogException } from '@core/helpers/sentryLogger';
import * as KPITypes from '../constants';

@Component
@MapActions(['calculateProductSales', 'addNotification'])
@MapGetters(['uncubesServerReady', 'kpiStorage', 'predictionPeriodList', 'pointOfSaleList'])
export default class ProductSalesTable extends Vue {
  @Prop({ type: Boolean, default: false }) isManaged;

  kpiType = KPITypes.PRODUCT_SALES;

  value = [];

  pageCount = 0;

  increment = null;

  get headers() {
    return [
      {
        text: this.$t('mostSoldItems'),
        align: 'start',
        sortable: false,
        value: 'name'
      },
      {
        text: this.$t('soldUnits'),
        align: 'end',
        sortable: false,
        value: 'units'
      },
      {
        text: this.$t('margin'),
        align: 'end',
        sortable: false,
        value: 'margin'
      }
    ];
  }

  get items() {
    if (this.value) {
      return this.value.map((el) => ({
        name: el[0],
        units: this.$n(el[1], { locale: 'de-DE' }),
        margin: this.$n((el[2] - el[3]) / el[1], {
          style: 'currency', currency: 'EUR', maximumFractionDigits: 2, minimumFractionDigits: 0
        })
      }));
    }
    return [];
  }

  get readyToCalc() {
    return this.uncubesServerReady && this.predictionPeriodList.length !== 0 && this.pointOfSaleList.length !== 0;
  }

  get extraResults() {
    return Math.floor(this.pageCount / 10) * 10;
  }

  onPage(event) {
    if ((event.page + 1) % 10 === 0) {
      this.reCalculate(100 + ((event.page + 1) * 10));
    }
  }

  created() {
    this.loadKPIModel();
    if (this.readyToCalc) this.runCalculate();
  }

  loadKPIModel() {
    const model = this.kpiStorage(this.kpiType)(`${this.$route.params.pointOfSaleGuid}-${this.isManaged}`);
    this.value = model?.kpi;
    this.increment = model?.increment;
  }

  async runCalculate() {
    try {
      await this.calculate();
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('KPILoadingError'), { id: 'KPIError' }));
      sentryLogException(err, 'Error loading one or more KPIs');
    }
  }

  async calculate() {
    await this.calculateProductSales({
      pointOfSaleGuid: this.$route.params.pointOfSaleGuid,
      isManaged: this.isManaged,
      extraResults: 0
    });
    this.loadKPIModel();
  }

  async reCalculate(extraResults) {
    await this.calculateProductSales({
      pointOfSaleGuid: this.$route.params.pointOfSaleGuid,
      isManaged: this.isManaged,
      extraResults
    });
    this.loadKPIModel();
  }

  @Watch('readyToCalc')
  async onReadyToCalc(val) {
    if (val) {
      await this.runCalculate();
    }
  }

  @Watch('isManaged')
  async onIsManaged() {
    await this.runCalculate();
  }
}
</script>

<style lang="scss">
#sales-table {
  width: 100%;

  div.v-data-footer {
    div.v-data-footer__select {
      display: none;
    }

    .v-data-footer__pagination {
      display: none;
    }
  }
}
</style>
